import { useCallback } from 'react';
import type { CreateShareRequest, CreateShareResponse, Permission } from '@markbird/shared';
import { useStore } from '../store/useStore';
import { getIdToken } from '../lib/auth';
import { rememberLocalShare } from '../lib/localShares';

export interface ShareApi {
  /** Create a share for the active doc. Defaults to a read-only link that never expires. */
  share: (
    permission?: Permission,
    expiresAt?: number | null,
    password?: string,
    slug?: string,
  ) => Promise<void>;
  lastUrl: string | null;
  busy: boolean;
  error: string | null;
  clear: () => void;
}

/**
 * Read-only share creation (path A). POSTs the active document to the
 * same-origin `/api/shares` and surfaces the returned share URL through the
 * store (so any trigger — toolbar or palette — shows one ShareDialog). The
 * request is shaped by `CreateShareRequest`, which already allows
 * `permission`/`expiresAt` (B-ready) — the MVP server forces read-only.
 */
export function useShare(): ShareApi {
  const lastUrl = useStore((s) => s.shareUrl);
  const busy = useStore((s) => s.shareBusy);
  const error = useStore((s) => s.shareError);

  const share = useCallback(
    async (
      permission: Permission = 'read',
      expiresAt: number | null = null,
      password?: string,
      slug?: string,
    ) => {
      const store = useStore.getState();
      const { activeDoc } = store;
      if (!activeDoc.content.trim()) {
        store.setShareResult(null, 'Nothing to share — the document is empty.');
        return;
      }
      store.setShareBusy(true);
      try {
        // An 'edit' share requires a signed-in owner (the server attributes it to
        // the uid and enforces auth). Attach the ID token when we have one.
        const headers: Record<string, string> = { 'content-type': 'application/json' };
        const token = permission === 'edit' ? await getIdToken() : null;
        if (token) headers.authorization = `Bearer ${token}`;

        const payload: CreateShareRequest = {
          title: activeDoc.title,
          content: activeDoc.content,
          permission: token ? permission : 'read',
          expiresAt,
          ...(password ? { password } : {}),
          ...(slug ? { slug } : {}),
        };
        const res = await fetch('/api/shares', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(body.message ?? `Share failed (${res.status}).`);
        }
        const data = (await res.json()) as CreateShareResponse;
        // The deletion secret comes back exactly once. Without keeping it, a
        // link made without an account could never be taken back.
        await rememberLocalShare({
          token: data.token,
          url: data.url,
          title: activeDoc.title,
          createdAt: Date.now(),
          expiresAt: data.expiresAt,
          deleteToken: data.deleteToken,
          deleteUrl: data.deleteUrl,
        });
        store.setShareResult(data.url, null, token ? permission : 'read', data.expiresAt);
        try {
          await navigator.clipboard.writeText(data.url);
        } catch {
          // Clipboard may be blocked; the URL is still shown for manual copy.
        }
      } catch (err) {
        store.setShareResult(null, err instanceof Error ? err.message : 'Share failed.');
      }
    },
    [],
  );

  const clear = useCallback(() => useStore.getState().clearShare(), []);

  return { share, lastUrl, busy, error, clear };
}
